import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/lib/queryKeys';
import { listAllergies, createAllergy, updateAllergy, retractAllergy } from './clinicalApi';
import type { AllergyInput } from './types';

/** Allergies are tenant-wide (patient-level, not branch-scoped) — see clinicalApi.ts. */
export function useAllergies(uhid: string, includeInactive = false) {
  return useQuery({
    queryKey: keys.clinical.allergies(uhid, includeInactive),
    queryFn: () => listAllergies(uhid, includeInactive),
    enabled: !!uhid,
  });
}

/** Invalidates both the active-only and include-inactive variants, since either could be mounted. */
function invalidateAllergies(queryClient: ReturnType<typeof useQueryClient>, uhid: string) {
  queryClient.invalidateQueries({ queryKey: keys.clinical.allergies(uhid, false) });
  queryClient.invalidateQueries({ queryKey: keys.clinical.allergies(uhid, true) });
}

export function useCreateAllergy(uhid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AllergyInput) => createAllergy(uhid, input),
    onSuccess: () => invalidateAllergies(queryClient, uhid),
  });
}

export function useUpdateAllergy(uhid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch, ifMatch }: { id: number; patch: Partial<AllergyInput>; ifMatch?: string }) =>
      updateAllergy(uhid, id, patch, ifMatch),
    onSuccess: () => invalidateAllergies(queryClient, uhid),
  });
}

export function useRetractAllergy(uhid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => retractAllergy(uhid, id),
    onSuccess: () => invalidateAllergies(queryClient, uhid),
  });
}
